import { Query } from '@apollo/client/react/components';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import React from 'react';

import { withMediaListener } from '@xingternal/util';

import { SimilarJobsContext } from '@/contexts';
import { useLoginStates } from '@/hooks';
import { jobShape } from '@/proptypes/job';

import { SIDEBAR_SIMILAR_JOBS_COUNT } from '../../constants';
import { hasSidebarJobs as getHasSidebarJobs, getSimilarJobsQueryLimit } from '../../helpers';
import similarJobs from './similar-jobs.query.graphql';

const LI_CONSUMER = 'loggedin.web.jobs.posting.similar';
const LO_CONSUMER = 'loggedout.web.jobs.posting.similar';

const removeFields = R.compose(
  R.dissocPath(['job', 'companyInfo', 'company', 'kununuData']),
  R.dissocPath(['job', 'salary'])
);
const transformSidebarJobsData = (jobs) => jobs.map(removeFields);

const normalizeSimilarJobs = ({ loading, error, data }, hasSidebarJobs) => {
  let similarJobs = {};
  const jobs = R.pathOr(null, ['viewer', 'similarJobs', 'collection'], data);
  if (!loading && !error && !R.isNil(jobs)) {
    similarJobs = hasSidebarJobs
      ? {
          sidebarJobs: transformSidebarJobsData(jobs.slice(0, SIDEBAR_SIMILAR_JOBS_COUNT)),
          footerJobs: jobs.slice(SIDEBAR_SIMILAR_JOBS_COUNT),
        }
      : { footerJobs: jobs };
  }

  return {
    loading,
    error,
    hasSidebarJobs,
    sidebarJobs: [],
    footerJobs: [],
    ...similarJobs,
  };
};

export function withSimilarJobs(WrappedComponent) {
  const Container = withMediaListener(({ matchedMedia, job, ...otherProps }) => {
    if (!job) return <WrappedComponent job={job} {...otherProps} />;

    const { isLoggedOut } = useLoginStates();

    const hasSidebarJobs = getHasSidebarJobs({ job, matchedMedia });
    const limit = getSimilarJobsQueryLimit(hasSidebarJobs);

    return (
      <Query
        query={similarJobs}
        variables={{
          id: job.id,
          consumer: isLoggedOut ? LO_CONSUMER : LI_CONSUMER,
          limit,
        }}
        errorPolicy="all"
      >
        {(result) => (
          <SimilarJobsContext.Provider value={normalizeSimilarJobs(result, hasSidebarJobs)}>
            <WrappedComponent job={job} {...otherProps} />
          </SimilarJobsContext.Provider>
        )}
      </Query>
    );
  });

  Container.propTypes = {
    job: jobShape,
    matchedMedia: PropTypes.string,
  };

  return Container;
}
